import React from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

import "./index.css";
import "antd/dist/reset.css";
import "./i18n";

// Provider
import { LanguageProvider } from "./LanguageProvider";
import { AuthProvider } from "./context/AuthContext";

// Pagine principali
// ❌ rimosso SweetTradingIntro
import SweetTradingPro from "./SweetTradingPro";         // versione inglese (landing principale)
import SweetTradingProIT from "./SweetTradingProIT.jsx"; // versione italiana statica
import BotsPage from "./BotsPage";
import GenIBotsPage from "./GenIBotsPage";
import CandyBotSection from "./CandyBotSection";
import Dashboard from "./components/Dashboard";
import ProtectedGuide from "./components/ProtectedGuide";
import Guida from "./Guida";
import Broker from "./broker";

// Auth
import Login from "./components/Login";
import Register from "./components/Register";
import ForgotPassword from "./components/ForgotPassword";
import PrivateRoute from "./components/PrivateRoute";

// Legal
import TerminiCondizioni from "./components/pages/TerminiCondizioni";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";

// traduzioni
import SweetTradingProDE from "./SweetTradingProDE.jsx";
import SweetTradingProES from "./SweetTradingProES.jsx";
import SweetTradingProFR from "./SweetTradingProFR.jsx";
import SweetTradingProAR from "./SweetTradingProAR.jsx";

// approfondimento why section
import StrategyPage from "./StrategyPage";

/* ======================= */
/*      SEO COMPONENT      */
/* ======================= */
function Seo() {
  const { pathname } = useLocation();

  // Normalizza il path per lookup (mantieni "/" intatto)
  const normPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");

  // lingua HTML dinamica in base al prefisso del path
  const langFromPath = (p) => {
    if (/^\/it(\/|$)/.test(p)) return "it";
    if (/^\/de(\/|$)/.test(p)) return "de";
    if (/^\/es(\/|$)/.test(p)) return "es";
    if (/^\/fr(\/|$)/.test(p)) return "fr";
    if (/^\/ar(\/|$)/.test(p)) return "ar";
    return "en";
  };
  const htmlLang = langFromPath(pathname);
  const htmlDir = htmlLang === "ar" ? "rtl" : "ltr";

  // Mappa titoli/description per route chiave (home per lingua + pagine principali)
  const meta = {
    "/": {
      title: "Sweet Trading | Advanced AI Trading Bot",
      desc:
        "Automate your trading with our AI-driven bot for Forex, stocks and crypto. 24/7 monitoring, risk management, and step-by-step setup.",
    },
    "/it": {
      title: "Sweet Trading | Bot di trading automatizzato",
      desc:
        "Automatizza le tue strategie su Forex, azioni e crypto con AI, gestione del rischio e guida passo passo. Supporto 24/7.",
    },
    "/de": {
      title: "Sweet Trading | Automatisierter KI-Trading-Bot",
      desc:
        "Automatisieren Sie Ihr Trading für Forex, Aktien und Krypto mit KI, Risikomanagement und Schritt-für-Schritt-Setup. 24/7-Überwachung.",
    },
    "/es": {
      title: "Sweet Trading | Bot de trading automatizado con IA",
      desc:
        "Automatiza tu operativa en Forex, acciones y cripto con IA, control de riesgo y guía paso a paso. Monitorización 24/7.",
    },
    "/fr": {
      title: "Sweet Trading | Bot de trading automatisé par IA",
      desc:
        "Automatisez vos stratégies sur Forex, actions et crypto avec IA, gestion du risque et accompagnement pas à pas. Surveillance 24/7.",
    },
    "/ar": {
      title: "سويت تريدينغ | روبوت تداول آلي بالذكاء الاصطناعي",
      desc:
        "أتمت تداولك في الفوركس والأسهم والعملات المشفرة مع إدارة مخاطر وإعداد إرشادي ومراقبة على مدار الساعة.",
    },
    "/home": {
      title: "Sweet Trading | Panoramica",
      desc:
        "Panoramica delle funzioni: strategie, risk management, integrazioni exchange e monitoring real-time.",
    },
    "/broker": {
      title: "Broker & partnership CPA | Sweet Trading",
      desc:
        "Requisiti, integrazioni e modelli di partnership/CPA per broker e affiliati.",
    },
    "/bots": {
      title: "Bots di trading | Sweet Trading",
      desc:
        "Crea e personalizza bot di trading automatizzato con indicatori, SL/TP e regole di sizing.",
    },
    "/bots/settings": {
      title: "Impostazioni bot | Sweet Trading",
      desc:
        "Configura parametri operativi, avvisi e limiti di rischio per i tuoi bot.",
    },
    "/terms": {
      title: "Termini e condizioni | Sweet Trading",
      desc: "Condizioni d'uso della piattaforma Sweet Trading.",
    },
    "/privacy": {
      title: "Privacy Policy | Sweet Trading",
      desc: "Informativa sul trattamento dei dati personali.",
    },
    "/login": {
      title: "Login | Sweet Trading",
      desc: "Accedi al tuo account.",
    },
    "/register": {
      title: "Registrati | Sweet Trading",
      desc: "Crea un nuovo account.",
    },
    "/forgot-password": {
      title: "Password dimenticata | Sweet Trading",
      desc: "Recupera l’accesso al tuo account.",
    },
    "/dashboard": {
      title: "Dashboard | Sweet Trading",
      desc: "La tua operatività e i KPI in tempo reale.",
    },
    "/protected-guide": {
      title: "Guida riservata | Sweet Trading",
      desc: "Documentazione accessibile agli utenti autorizzati.",
    },
    "/guide": {
      title: "Guida | Sweet Trading",
      desc: "Manuale d'uso e best practice.",
    },
  };

  const defaultTitle = "Sweet Trading";
  const defaultDesc =
    "AI trading bot with risk management, live monitoring and guided setup.";
  const current = meta[normPath] || { title: defaultTitle, desc: defaultDesc };

  // Noindex per aree riservate/autenticazione (+ sottopercorsi)
  const noindexBases = [
    "/dashboard",
    "/protected-guide",
    "/guide",
    "/login",
    "/register",
    "/forgot-password",
  ];
  const shouldNoindex = noindexBases.some(
    (p) => normPath === p || normPath.startsWith(p + "/")
  );
  const robots = shouldNoindex ? "noindex,nofollow" : "index,follow";

  // Canonical: dominio + pathname normalizzato con slash finale
  const base = "https://www.sweet-trading.com";
  const withTrailingSlash =
    pathname.endsWith("/") ? pathname : pathname + "/";
  const canonical = `${base}${withTrailingSlash}`;

  // Hreflang: solo per root multilingua (/, /it, /de, /es, /fr, /ar)
  const isLocaleRoot = /^\/(it|de|es|fr|ar)?\/?$/.test(pathname);
  const locales = [
    { code: "en", path: "/" },
    { code: "it", path: "/it/" },
    { code: "de", path: "/de/" },
    { code: "es", path: "/es/" },
    { code: "fr", path: "/fr/" },
    { code: "ar", path: "/ar/" },
  ];

  // OG locale mapping
  const ogLocaleMap = {
    en: "en_US",
    it: "it_IT",
    de: "de_DE",
    es: "es_ES",
    fr: "fr_FR",
    ar: "ar",
  };
  const currentOgLocale = ogLocaleMap[htmlLang] || "en_US";
  const alternateOgLocales = Object.keys(ogLocaleMap)
    .filter((k) => k !== htmlLang)
    .map((k) => ogLocaleMap[k]);

  // JSON-LD: solo SoftwareApplication in "/" (Organization/WebSite già in index.html)
  const ldApp =
    normPath === "/"
      ? {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Sweet Trading",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: base + "/",
          description:
            "AI-driven automated trading bot for Forex, stocks and crypto with risk control and live monitoring.",
          image: base + "/og-image.jpg",
          publisher: {
            "@type": "Organization",
            name: "Sweet Trading",
            url: base + "/",
          },
        }
      : null;

  const ogImage = `${base}/og-image.jpg`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={htmlLang} dir={htmlDir} />
      <title>{current.title}</title>
      <meta name="description" content={current.desc} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      {/* Hreflang alternates solo sulle root */}
      {isLocaleRoot &&
        locales.map((l) => (
          <link
            key={l.code}
            rel="alternate"
            hrefLang={l.code}
            href={base + l.path}
          />
        ))}
      {isLocaleRoot && (
        <link rel="alternate" hrefLang="x-default" href={base + "/"} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Sweet Trading" />
      <meta property="og:title" content={current.title} />
      <meta property="og:description" content={current.desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content="Sweet Trading dashboard preview" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={currentOgLocale} />
      {alternateOgLocales.map((loc) => (
        <meta key={loc} property="og:locale:alternate" content={loc} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={current.title} />
      <meta name="twitter:description" content={current.desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@sweettrading" />

      {/* JSON-LD solo in "/" */}
      {ldApp && (
        <script type="application/ld+json">{JSON.stringify(ldApp)}</script>
      )}
    </Helmet>
  );
}

/* ======================= */
/*        APP ROOT         */
/* ======================= */
function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <HelmetProvider>
          <Router>
            {/* SEO dinamico su ogni cambio route */}
            <Seo />

            <Routes>
              {/* PUBBLICHE */}

              {/* ROOT in inglese: va DIRETTO a SweetTradingPro */}
              <Route path="/" element={<SweetTradingPro />} />

              <Route path="/strategy" element={<StrategyPage />} />
              <Route path="/strategy/" element={<StrategyPage />} />

              {/* VERSIONI STATICHE PER LINGUA */}
              <Route path="/it" element={<SweetTradingProIT />} />
              <Route path="/it/" element={<SweetTradingProIT />} />
              <Route path="/de" element={<SweetTradingProDE />} />
              <Route path="/de/" element={<SweetTradingProDE />} />
              <Route path="/es" element={<SweetTradingProES />} />
              <Route path="/es/" element={<SweetTradingProES />} />
              <Route path="/fr" element={<SweetTradingProFR />} />
              <Route path="/fr/" element={<SweetTradingProFR />} />
              <Route path="/ar" element={<SweetTradingProAR />} />
              <Route path="/ar/" element={<SweetTradingProAR />} />

              <Route path="/home" element={<HomePage />} />
              <Route path="/broker" element={<Broker />} />
              <Route path="/bots" element={<BotsPage />} />
              <Route path="/bots/settings" element={<GenIBotsPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/terms" element={<TerminiCondizioni />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />

              {/* PROTETTE */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/protected-guide"
                element={
                  <PrivateRoute>
                    <ProtectedGuide />
                  </PrivateRoute>
                }
              />
              <Route
                path="/guide"
                element={
                  <PrivateRoute>
                    <Guida />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Router>
        </HelmetProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

// HomePage wrapper
function HomePage() {
  return (
    <div>
      {/* H1 semantico per SEO */}
      <h1 style={{ position: "absolute", left: "-9999px" }}>
        Bot di trading avanzato per crypto e investimenti
      </h1>
      <CandyBotSection />
    </div>
  );
}

// Render App
ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
